import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Home, MapPin, Bed, Bath, Square, Calendar, DollarSign, TrendingUp, Mail, Phone, User, MessageSquare, Eye, ChevronLeft, ChevronRight, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListingDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [inquiryForm, setInquiryForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    message: '',
    // NEW: Matching agent request fields for filtering
    urgency: 'flexible',
    timeline_to_buy: '',
    pre_approved: '',
    pre_approved_amount: '',
    preferred_contact_method: 'email',
    preferred_contact_time: 'anytime',
    need_agent_help: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
      setNotFound(false);
    } catch (error) {
      console.error('Listing not found:', id);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const submitInquiry = async () => {
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.message) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        listing_id: id,
        ...inquiryForm
      });
      toast.success('Inquiry sent successfully!');
      setInquiryForm({ 
        name: user?.name || '', 
        email: user?.email || '', 
        phone: '', 
        message: '',
        urgency: 'flexible',
        timeline_to_buy: '',
        pre_approved: '',
        pre_approved_amount: '',
        preferred_contact_method: 'email',
        preferred_contact_time: 'anytime',
        need_agent_help: false
      });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  // Show 404 page for non-existent listings (SEO-friendly, no redirect)
  if (notFound || !listing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
        <Helmet>
          <title>Listing Not Found | Its-Sold.com</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        {/* Navigation */}
        <nav className="bg-white border-b border-orange-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Home className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
              </Link>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-10">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Listing Not Found</h1>
            <p className="text-lg text-gray-600 mb-8">
              This property listing may have been sold, expired, or removed by the owner.
              Check out other available homes in the DFW area!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/search')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3"
              >
                <Search className="w-5 h-5 mr-2" />
                Browse All Listings
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="px-8 py-3"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Homepage
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/search')} variant="outline" data-testid="back-to-search-btn">
              Back to Search
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Demo Listing Banner */}
        {listing.is_seed_listing && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 shadow-lg">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-blue-900 mb-2">
                  Demo Listing - For Display Purposes Only
                </h3>
                <p className="text-blue-800 mb-3">
                  <strong>Welcome to the relaunched Its-Sold.com!</strong> This listing is here to show you how your property will appear on our platform and to help stimulate marketplace activity. 
                </p>
                <p className="text-blue-700 text-sm">
                  These demo listings feature placeholder images to demonstrate the complete photo gallery experience. When you create your listing, you'll upload real photos of your property. <span className="font-semibold">This is not an active property for sale.</span>
                </p>
                <div className="mt-4">
                  <Button 
                    onClick={() => navigate('/create')} 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Create Your Real Listing
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-orange-100">
              {listing.photos && listing.photos.length > 0 ? (
                <div className="relative">
                  <img
                    src={listing.photos[currentPhotoIndex]}
                    alt={listing.title}
                    className="w-full h-96 object-cover"
                    data-testid="listing-main-photo"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800x600/f97316/FFFFFF?text=No+Image';
                    }}
                  />
                  {listing.photos.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + listing.photos.length) % listing.photos.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white"
                        data-testid="photo-prev-btn"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % listing.photos.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white"
                        data-testid="photo-next-btn"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {currentPhotoIndex + 1} / {listing.photos.length}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200">
                  <Home className="w-32 h-32 text-orange-400" />
                </div>
              )}
              {listing.photos && listing.photos.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {listing.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo}
                      alt={`Thumbnail ${idx + 1}`}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`w-20 h-20 object-cover rounded-lg cursor-pointer border-2 ${currentPhotoIndex === idx ? 'border-orange-600' : 'border-transparent'}`}
                      data-testid={`thumbnail-${idx}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x200/f97316/FFFFFF?text=No+Image';
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }} data-testid="listing-title">
                    {listing.title}
                  </h1>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span className="text-lg" data-testid="listing-address">{listing.address}, {listing.city}, TX {listing.zip_code}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{listing.county} County</span>
                    <span className="text-gray-300">•</span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Eye className="w-4 h-4 mr-1" />
                      {listing.views} views
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-orange-600 mb-2" style={{ fontFamily: 'Playfair Display' }} data-testid="listing-price">
                    ${listing.price.toLocaleString()}
                  </div>
                  {listing.suggested_price && (
                    <div className="text-sm text-gray-500 flex items-center justify-end">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      AI Estimate: ${listing.suggested_price.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-8 pb-8 border-b border-gray-200">
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <Bed className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                  <div className="text-2xl font-bold text-gray-900" data-testid="listing-bedrooms">{listing.bedrooms}</div>
                  <div className="text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <Bath className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-2xl font-bold text-gray-900" data-testid="listing-bathrooms">{listing.bathrooms}</div>
                  <div className="text-sm text-gray-600">Bathrooms</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <Square className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <div className="text-2xl font-bold text-gray-900" data-testid="listing-sqft">{listing.sqft.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Sqft</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <div className="text-2xl font-bold text-gray-900" data-testid="listing-year">{listing.year_built || 'N/A'}</div>
                  <div className="text-sm text-gray-600">Year Built</div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>Description</h3>
                  <p className="text-gray-700 leading-relaxed" data-testid="listing-description">{listing.description}</p>
                </div>

                {listing.ai_description && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl border border-purple-100">
                    <div className="flex items-center mb-3">
                      <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">AI Enhanced</div>
                    </div>
                    <p className="text-gray-700 leading-relaxed" data-testid="listing-ai-description">{listing.ai_description}</p>
                  </div>
                )}

                {listing.features && listing.features.length > 0 && (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>Features</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {listing.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-gray-700">
                          <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                          <span data-testid={`feature-${idx}`}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Comprehensive Property Details */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display' }}>Complete Property Details</h2>
              
              {/* Property Overview */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Home className="w-6 h-6 mr-2 text-orange-600" />
                  Property Overview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-orange-50 p-4 rounded-lg">
                  <div><span className="font-semibold">Property Type:</span> {listing.property_type}</div>
                  <div><span className="font-semibold">Year Built:</span> {listing.year_built || 'N/A'}</div>
                  <div><span className="font-semibold">Square Footage:</span> {listing.sqft?.toLocaleString()} sqft</div>
                  {listing.lot_size && (
                    <div>
                      <span className="font-semibold">Lot Size:</span>{' '}
                      {listing.lot_size > 100 ? (
                        // Already in sqft (old data)
                        <>
                          {listing.lot_size.toLocaleString()} sqft
                          <span className="text-gray-500 text-sm ml-1">({(listing.lot_size / 43560).toFixed(2)} acres)</span>
                        </>
                      ) : listing.lot_size > 1.5 ? (
                        // Too large - cap display at 1.5 acres max
                        <>
                          {(1.5 * 43560).toLocaleString()} sqft
                          <span className="text-gray-500 text-sm ml-1">(1.5 acres max)</span>
                        </>
                      ) : (
                        // Normal acres value (new data)
                        <>
                          {(listing.lot_size * 43560).toLocaleString(0)} sqft
                          <span className="text-gray-500 text-sm ml-1">({listing.lot_size} acres)</span>
                        </>
                      )}
                    </div>
                  )}
                  {listing.zoning && <div><span className="font-semibold">Zoning:</span> {listing.zoning}</div>}
                  {listing.framing_type && <div><span className="font-semibold">Framing:</span> {listing.framing_type}</div>}
                </div>
              </div>
              
              {/* Financial Information - ALWAYS SHOW for transparency */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-6 h-6 mr-2 text-green-600" />
                  Financial Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                  <div className="col-span-2 bg-white p-3 rounded border-l-4 border-green-600">
                    <div className="text-2xl font-bold text-gray-900">${listing.price?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Listing Price</div>
                  </div>
                  {listing.annual_property_tax ? (
                    <div><span className="font-semibold">Annual Property Tax:</span> ${listing.annual_property_tax.toLocaleString()}</div>
                  ) : (
                    <div><span className="font-semibold">Annual Property Tax:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                  {listing.taxing_authority && (
                    <div><span className="font-semibold">Taxing Authority:</span> {listing.taxing_authority}</div>
                  )}
                  {listing.annual_home_insurance ? (
                    <div><span className="font-semibold">Annual Home Insurance:</span> ${listing.annual_home_insurance.toLocaleString()}</div>
                  ) : (
                    <div><span className="font-semibold">Annual Home Insurance:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                  {listing.monthly_home_insurance && (
                    <div><span className="font-semibold">Monthly Insurance:</span> ${listing.monthly_home_insurance.toLocaleString()}</div>
                  )}
                  {listing.hoa_fee ? (
                    <div><span className="font-semibold">HOA Fee:</span> ${listing.hoa_fee.toLocaleString()} {listing.hoa_fee_frequency || 'monthly'}</div>
                  ) : (
                    <div><span className="font-semibold">HOA Fee:</span> <span className="text-gray-500">None</span></div>
                  )}
                  <div><span className="font-semibold">Owner Financing:</span> {listing.owner_financing ? 'Available' : 'Not Available'}</div>
                  {listing.assumable_loan && listing.assumable_loan !== 'None' && (
                    <div><span className="font-semibold">Assumable Loan:</span> {listing.assumable_loan}</div>
                  )}
                </div>
              </div>

              {/* Pool & Outdoor */}
              {listing.has_pool && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Pool & Outdoor Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                    <div><span className="font-semibold">Pool Type:</span> {listing.pool_type || 'Yes'}</div>
                    {listing.pool_size && <div><span className="font-semibold">Pool Size:</span> {listing.pool_size}</div>}
                    {listing.pool_features && listing.pool_features.length > 0 && (
                      <div className="col-span-2"><span className="font-semibold">Pool Features:</span> {listing.pool_features.join(', ')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Garage */}
              {listing.garage_spaces && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Garage & Parking</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div>
                      <span className="font-semibold">Garage:</span> {listing.garage_spaces}-car {listing.garage_type?.toLowerCase() || 'garage'}
                    </div>
                  </div>
                </div>
              )}

              {/* Property Systems */}
              {(listing.hvac_age || listing.roof_age || listing.foundation_type) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Structure & Systems</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    {listing.hvac_age && <div><span className="font-semibold">HVAC Age:</span> {listing.hvac_age} years</div>}
                    {listing.hvac_type && <div><span className="font-semibold">HVAC Type:</span> {listing.hvac_type}</div>}
                    {listing.heating_fuel && <div><span className="font-semibold">Heating Fuel:</span> {listing.heating_fuel}</div>}
                    {listing.roof_age && <div><span className="font-semibold">Roof Age:</span> {listing.roof_age} years</div>}
                    {listing.roof_material && <div><span className="font-semibold">Roof Material:</span> {listing.roof_material}</div>}
                    {listing.roof_warranty_remaining && <div><span className="font-semibold">Roof Warranty:</span> {listing.roof_warranty_remaining}</div>}
                    {listing.foundation_type && <div><span className="font-semibold">Foundation:</span> {listing.foundation_type}</div>}
                    {listing.foundation_issues && <div><span className="font-semibold">Foundation Issues:</span> {listing.foundation_issues}</div>}
                    {listing.exterior_material && listing.exterior_material.length > 0 && (
                      <div className="col-span-2"><span className="font-semibold">Exterior Material:</span> {listing.exterior_material.join(', ')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Utilities */}
              {(listing.water_source || listing.sewer_system || listing.utilities) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Utilities & Energy</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                    {listing.water_source && <div><span className="font-semibold">Water Source:</span> {listing.water_source}</div>}
                    {listing.sewer_system && <div><span className="font-semibold">Sewer:</span> {listing.sewer_system}</div>}
                    {listing.electrical_type && <div><span className="font-semibold">Electrical:</span> {listing.electrical_type}</div>}
                    {listing.plumbing_type && <div><span className="font-semibold">Plumbing:</span> {listing.plumbing_type}</div>}
                    {listing.has_solar_panels && (
                      <>
                        <div><span className="font-semibold">Solar Panels:</span> Yes ({listing.solar_panel_year_installed})</div>
                        <div><span className="font-semibold">Solar Ownership:</span> {listing.solar_panel_ownership}</div>
                      </>
                    )}
                    {listing.monthly_electric_cost && <div><span className="font-semibold">Monthly Electric:</span> ${listing.monthly_electric_cost}</div>}
                    {listing.monthly_gas_cost && <div><span className="font-semibold">Monthly Gas:</span> ${listing.monthly_gas_cost}</div>}
                    {listing.monthly_water_sewer_cost && <div><span className="font-semibold">Monthly Water/Sewer:</span> ${listing.monthly_water_sewer_cost}</div>}
                  </div>
                </div>
              )}

              {/* Interior Details */}
              {(listing.flooring_types || listing.carpet_age || listing.kitchen_renovation_date) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Interior Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    {listing.flooring_types && listing.flooring_types.length > 0 && (
                      <div className="col-span-2"><span className="font-semibold">Flooring:</span> {listing.flooring_types.join(', ')}</div>
                    )}
                    {listing.carpet_age && <div><span className="font-semibold">Carpet Age:</span> {listing.carpet_age} years</div>}
                    {listing.kitchen_renovation_date && listing.kitchen_renovation_date !== 'Never' && (
                      <div><span className="font-semibold">Kitchen Renovated:</span> {listing.kitchen_renovation_date}</div>
                    )}
                    {listing.bathroom_renovation_date && listing.bathroom_renovation_date !== 'Never' && (
                      <div><span className="font-semibold">Bathroom Renovated:</span> {listing.bathroom_renovation_date}</div>
                    )}
                    {listing.full_renovation_date && listing.full_renovation_date !== 'Never' && (
                      <div><span className="font-semibold">Full Renovation:</span> {listing.full_renovation_date}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Schools - ALWAYS SHOW */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Schools & Education</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50 p-4 rounded-lg">
                  {listing.school_district ? (
                    <div className="col-span-2 bg-white p-3 rounded border-l-4 border-indigo-600">
                      <span className="font-semibold">School District:</span> {listing.school_district}
                    </div>
                  ) : (
                    <div className="col-span-2"><span className="font-semibold">School District:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                  {listing.elementary_school ? (
                    <div><span className="font-semibold">Elementary:</span> {listing.elementary_school}</div>
                  ) : (
                    <div><span className="font-semibold">Elementary:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                  {listing.middle_school ? (
                    <div><span className="font-semibold">Middle School:</span> {listing.middle_school}</div>
                  ) : (
                    <div><span className="font-semibold">Middle School:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                  {listing.high_school ? (
                    <div><span className="font-semibold">High School:</span> {listing.high_school}</div>
                  ) : (
                    <div><span className="font-semibold">High School:</span> <span className="text-gray-500">Not provided</span></div>
                  )}
                </div>
              </div>
              
              {/* Appliances & Inclusions */}
              {(listing.appliances_included && listing.appliances_included.length > 0) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Appliances Included</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {listing.appliances_included.map((appliance, idx) => (
                        <span key={idx} className="bg-white px-3 py-1 rounded-full text-sm border border-gray-300">
                          {appliance}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Recent Upgrades & Improvements */}
              {listing.recent_upgrades && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Upgrades & Improvements</h3>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700">{listing.recent_upgrades}</p>
                  </div>
                </div>
              )}
              
              {/* Legal & Property Rights */}
              {(listing.mineral_rights || listing.restrictions || listing.survey_available) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Legal & Property Rights</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    {listing.mineral_rights && (
                      <div><span className="font-semibold">Mineral Rights:</span> {listing.mineral_rights}</div>
                    )}
                    {listing.restrictions && (
                      <div className="col-span-2"><span className="font-semibold">Restrictions:</span> {listing.restrictions}</div>
                    )}
                    {listing.survey_available && (
                      <div><span className="font-semibold">Survey:</span> {listing.survey_available}</div>
                    )}
                    {listing.survey_date && (
                      <div><span className="font-semibold">Survey Date:</span> {listing.survey_date}</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Disclosures & Condition */}
              {(listing.sold_as_is || listing.known_issues || listing.recent_repairs) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Disclosures & Condition</h3>
                  <div className="bg-yellow-50 p-4 rounded-lg space-y-3 border border-yellow-200">
                    {listing.sold_as_is && (
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                        <span className="font-semibold">Sold As-Is</span>
                      </div>
                    )}
                    <div><span className="font-semibold">Seller Disclosure:</span> {listing.seller_disclosure_available ? 'Available' : 'Not Available'}</div>
                    {listing.inspection_responsibility && (
                      <div><span className="font-semibold">Inspection:</span> {listing.inspection_responsibility}</div>
                    )}
                    {listing.known_issues && (
                      <div className="col-span-2 pt-2 border-t border-yellow-200">
                        <span className="font-semibold">Known Issues:</span>
                        <p className="mt-1 text-gray-700">{listing.known_issues}</p>
                      </div>
                    )}
                    {listing.recent_repairs && (
                      <div className="col-span-2 pt-2 border-t border-yellow-200">
                        <span className="font-semibold">Recent Repairs:</span>
                        <p className="mt-1 text-gray-700">{listing.recent_repairs}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Seller Information */}
              {(listing.seller_motivation || listing.ideal_close_date) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Seller Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                    {listing.seller_motivation && (
                      <div><span className="font-semibold">Seller Motivation:</span> {listing.seller_motivation}</div>
                    )}
                    {listing.ideal_close_date && (
                      <div><span className="font-semibold">Ideal Close Date:</span> {listing.ideal_close_date}</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Virtual Tour & Media */}
              {(listing.video_url || listing.virtual_tour_url) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Virtual Tour & Videos</h3>
                  <div className="space-y-3">
                    {listing.video_url && (
                      <a href={listing.video_url} target="_blank" rel="noopener noreferrer" className="block bg-red-50 p-4 rounded-lg hover:bg-red-100 transition">
                        <div className="flex items-center space-x-2">
                          <span className="text-red-600 font-semibold">📹 Video Tour</span>
                          <span className="text-sm text-gray-600">(Opens in new tab)</span>
                        </div>
                      </a>
                    )}
                    {listing.virtual_tour_url && (
                      <a href={listing.virtual_tour_url} target="_blank" rel="noopener noreferrer" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition">
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-600 font-semibold">🏠 Virtual Tour</span>
                          <span className="text-sm text-gray-600">(Opens in new tab)</span>
                        </div>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Lot & Property */}
              {(listing.lot_size || listing.zoning) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Lot & Property Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    {listing.lot_size && (
                      <div>
                        <span className="font-semibold">Lot Size:</span>{' '}
                        {listing.lot_size > 100 ? (
                          // Already in sqft
                          <>
                            {listing.lot_size.toLocaleString()} sqft
                            <span className="text-gray-600 text-sm ml-2">({(listing.lot_size / 43560).toFixed(2)} acres)</span>
                          </>
                        ) : listing.lot_size > 1.5 ? (
                          // Cap at 1.5 acres
                          <>
                            {(1.5 * 43560).toLocaleString()} sqft
                            <span className="text-gray-600 text-sm ml-2">(1.5 acres max)</span>
                          </>
                        ) : (
                          // Normal acres
                          <>
                            {(listing.lot_size * 43560).toLocaleString()} sqft
                            <span className="text-gray-600 text-sm ml-2">({listing.lot_size} acres)</span>
                          </>
                        )}
                      </div>
                    )}
                    {listing.zoning && <div><span className="font-semibold">Zoning:</span> {listing.zoning}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-orange-100 sticky top-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display' }}>Contact FSBO Seller Directly</h3>
              <div className="space-y-4">
                {/* Contact Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                  <Input
                    value={inquiryForm.name}
                    onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                    placeholder="John Doe"
                    data-testid="inquiry-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="inquiry-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <Input
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="inquiry-phone-input"
                  />
                </div>

                {/* NEW: Buyer Intent & Timeline */}
                <div className="pt-3 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">How soon are you looking to buy? *</label>
                  <Select value={inquiryForm.urgency} onValueChange={(val) => setInquiryForm({...inquiryForm, urgency: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">🔴 Ready to buy now</SelectItem>
                      <SelectItem value="2_weeks">🟡 Within 2 weeks</SelectItem>
                      <SelectItem value="1_month">🟢 Within 1 month</SelectItem>
                      <SelectItem value="flexible">⚪ Flexible / Exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Financing Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Are you pre-approved for a loan? *</label>
                  <Select value={inquiryForm.pre_approved} onValueChange={(val) => setInquiryForm({...inquiryForm, pre_approved: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, pre-approved</SelectItem>
                      <SelectItem value="no">No, not yet</SelectItem>
                      <SelectItem value="cash">Cash buyer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {inquiryForm.pre_approved === 'yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pre-Approved Amount</label>
                    <Input
                      type="number"
                      value={inquiryForm.pre_approved_amount}
                      onChange={(e) => setInquiryForm({...inquiryForm, pre_approved_amount: e.target.value})}
                      placeholder="$500,000"
                    />
                  </div>
                )}

                {/* Contact Preferences */}
                <div className="pt-3 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method *</label>
                  <Select value={inquiryForm.preferred_contact_method} onValueChange={(val) => setInquiryForm({...inquiryForm, preferred_contact_method: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">📧 Email</SelectItem>
                      <SelectItem value="phone">📞 Phone</SelectItem>
                      <SelectItem value="sms">💬 Text/SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Best Time to Contact *</label>
                  <Select value={inquiryForm.preferred_contact_time} onValueChange={(val) => setInquiryForm({...inquiryForm, preferred_contact_time: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am-12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm-5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm-8pm)</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Agent Help Option */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={inquiryForm.need_agent_help}
                      onChange={(e) => setInquiryForm({...inquiryForm, need_agent_help: e.target.checked})}
                      className="mr-2 mt-1 h-4 w-4"
                    />
                    <span className="text-sm">
                      <strong>I'd like help from a licensed agent</strong>
                      <p className="text-xs text-gray-600 mt-1">We can connect you with vetted DFW agents</p>
                    </span>
                  </label>
                </div>

                {/* Message */}
                <div className="pt-3 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message to Seller *</label>
                  <Textarea
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                    placeholder="I'm interested in this property..."
                    rows={3}
                    data-testid="inquiry-message-input"
                  />
                </div>

                <Button
                  onClick={submitInquiry}
                  disabled={submitting}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="inquiry-submit-btn"
                >
                  {submitting ? 'Sending...' : 'Send Inquiry'}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  Your contact info stays private until seller responds
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Seller Information</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    <span data-testid="seller-name">{listing.contact_name}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    <span data-testid="seller-email">{listing.contact_email}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    <span data-testid="seller-phone">{listing.contact_phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;